import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

function need(obj,name,method){
  if(!obj||typeof obj[method]!=='function') throw Error(name+'_REQUIRED');
  return obj;
}
function normalizeStatus(s){
  return ['READY','REVIEW','BLOCK'].includes(s)?s:'REVIEW';
}
function worst(a,b){
  if(a==='BLOCK'||b==='BLOCK') return 'BLOCK';
  if(a==='REVIEW'||b==='REVIEW') return 'REVIEW';
  return 'READY';
}
function safeRun(fn,args,label,issues){
  try{return {ok:true,value:fn(...args)}}
  catch(e){
    issues.push({stage:label,severity:'REVIEW',message:String(e.message||e)});
    return {ok:false,value:null};
  }
}
async function safeRunAsync(fn,args,label,issues){
  try{return {ok:true,value:await fn(...args)}}
  catch(e){
    issues.push({stage:label,severity:'REVIEW',message:String(e.message||e)});
    return {ok:false,value:null};
  }
}

function create(opts={}){
  const pkgFactory=need(opts.packageFactory||g.ZooInvestCompanyResearchDataPackage,'PACKAGE_FACTORY','create');
  const pipeline=need(opts.pipeline||g.ZooInvestResearchRuntimePipelineOrchestrator,'PIPELINE','create');

  const pkgRuntime=pkgFactory.create(opts.packageOptions||{});
  const pipelineRuntime=pipeline.create(opts.pipelineOptions||{});

  async function run(req={}){
    const symbol=String(req.symbol||'').trim().toUpperCase();
    if(!symbol) throw Error('SYMBOL_REQUIRED');

    const issues=[];
    const pkgResult=await safeRunAsync(
      pkgRuntime.buildCompanyPackage.bind(pkgRuntime),
      [{...req,symbol}],
      'COMPANY_PACKAGE',
      issues
    );

    if(!pkgResult.ok){
      return Object.freeze({
        version:VERSION,symbol,status:'REVIEW',
        package:null,pipeline:null,issues:Object.freeze(issues)
      });
    }

    const companyPackage=pkgResult.value;
    const packageStatus=normalizeStatus(companyPackage.status);

    const researchInput={
      symbol,
      scope:req.scope||'RESEARCH',
      companyResearch:companyPackage.companyResearch,
      sourceState:companyPackage.sourceState,
      providerResults:companyPackage.providerResults,
      packageIssues:companyPackage.issues,
      packageGuards:companyPackage.guards
    };

    const pipeResult=await safeRunAsync(
      pipelineRuntime.run.bind(pipelineRuntime),
      [researchInput],
      'RESEARCH_PIPELINE',
      issues
    );

    if(!pipeResult.ok){
      return Object.freeze({
        version:VERSION,symbol,status:worst(packageStatus,'REVIEW'),
        package:companyPackage,pipeline:null,researchInput,
        issues:Object.freeze(issues)
      });
    }

    const pipe=pipeResult.value;
    const pipelineStatus=normalizeStatus(pipe.status||pipe.state||'REVIEW');
    const finalStatus=worst(packageStatus,pipelineStatus);

    return Object.freeze({
      version:VERSION,
      symbol,
      status:finalStatus,
      package:companyPackage,
      pipeline:pipe,
      researchInput:Object.freeze(researchInput),
      issues:Object.freeze(issues),
      guards:Object.freeze({
        packageReadyDoesNotOverridePipelineReview:true,
        pipelineReadyDoesNotOverridePackageReview:true,
        noUniversalScore:true,
        noTradeSignal:true,
        noCapitalPermission:true
      })
    });
  }

  return Object.freeze({VERSION,run});
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const packageFactory={
    create:()=>({
      async buildCompanyPackage(req){
        return {
          status:'READY',symbol:req.symbol,
          companyResearch:{
            canonicalFinancials:{symbol:req.symbol},
            financialHealth:{autoScore:false},
            financialHistory:{metrics:{}},
            growthTrajectory:{autoScore:false},
            marketDaily:{rows:[{date:'2026-08-29',close:100}]}
          },
          sourceState:{fundamentals:{state:'VERIFIED'},market:{state:'VERIFIED'}},
          providerResults:[{status:'READY'}],
          issues:[],
          guards:{noUniversalScore:true}
        };
      }
    })
  };

  let captured=null;
  const pipeline={
    create:()=>({
      async run(input){
        captured=input;
        return {status:'READY',snapshot:{symbol:input.symbol},brief:{attention:'NORMAL'}};
      }
    })
  };

  const api=create({packageFactory,pipeline});
  const r=await api.run({symbol:'aaa',scope:'OWNED'});
  ok('symbol normalized',r.symbol==='AAA');
  ok('ready package + ready pipeline => ready',r.status==='READY');
  ok('package retained',r.package.symbol==='AAA');
  ok('pipeline retained',r.pipeline.snapshot.symbol==='AAA');
  ok('scope forwarded',captured.scope==='OWNED');
  ok('company research forwarded',captured.companyResearch.marketDaily.rows[0].close===100);
  ok('source state forwarded',captured.sourceState.fundamentals.state==='VERIFIED');
  ok('provider results forwarded',captured.providerResults.length===1);
  ok('package guards forwarded',captured.packageGuards.noUniversalScore===true);
  ok('bridge guard no universal score',r.guards.noUniversalScore===true);
  ok('bridge guard no trade signal',r.guards.noTradeSignal===true);
  ok('bridge guard no capital permission',r.guards.noCapitalPermission===true);

  const reviewPackageFactory={
    create:()=>({
      async buildCompanyPackage(req){
        return {
          status:'REVIEW',symbol:req.symbol,companyResearch:{},sourceState:{},
          providerResults:[],issues:[{message:'MISSING'}],guards:{}
        };
      }
    })
  };
  const rr=create({packageFactory:reviewPackageFactory,pipeline});
  const review=await rr.run({symbol:'BBB'});
  ok('pipeline ready cannot erase package review',review.status==='REVIEW');

  const reviewPipeline={
    create:()=>({async run(){return {status:'REVIEW',reason:'CHECK'}}})
  };
  const rp=create({packageFactory,pipeline:reviewPipeline});
  const pr=await rp.run({symbol:'CCC'});
  ok('package ready cannot erase pipeline review',pr.status==='REVIEW');

  const blockPipeline={
    create:()=>({async run(){return {status:'BLOCK',reason:'CONTRACT'}}})
  };
  const bp=create({packageFactory,pipeline:blockPipeline});
  const br=await bp.run({symbol:'DDD'});
  ok('pipeline block propagates',br.status==='BLOCK');

  const failedPackage={
    create:()=>({async buildCompanyPackage(){throw Error('PACKAGE_FAIL')}})
  };
  const fp=create({packageFactory:failedPackage,pipeline});
  const fr=await fp.run({symbol:'EEE'});
  ok('package exception contained',fr.status==='REVIEW');
  ok('package failure issue recorded',fr.issues.some(x=>x.stage==='COMPANY_PACKAGE'));
  ok('pipeline skipped after package failure',fr.pipeline===null);

  const failedPipeline={
    create:()=>({async run(){throw Error('PIPE_FAIL')}})
  };
  const fpi=create({packageFactory,pipeline:failedPipeline});
  const pir=await fpi.run({symbol:'FFF'});
  ok('pipeline exception contained',pir.status==='REVIEW');
  ok('pipeline failure issue recorded',pir.issues.some(x=>x.stage==='RESEARCH_PIPELINE'));
  ok('package preserved after pipeline failure',pir.package.symbol==='FFF');

  let se=false;try{await api.run({symbol:''})}catch(e){se=e.message==='SYMBOL_REQUIRED'}
  ok('symbol required',se);

  let pe=false;try{create({packageFactory:{},pipeline})}catch(e){pe=e.message==='PACKAGE_FACTORY_REQUIRED'}
  ok('package factory required',pe);

  let ple=false;try{create({packageFactory,pipeline:{}})}catch(e){ple=e.message==='PIPELINE_REQUIRED'}
  ok('pipeline required',ple);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestCompanyPackageResearchPipelineBridge=Object.freeze({VERSION,create,selfTest});
})(engineContext);